import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Plus, Trash2, Link2, KeyRound, Loader2, Twitter, RefreshCw, ExternalLink, Globe, Zap, Linkedin, Facebook, Store, CheckCircle2, AlertTriangle, ShieldAlert, Activity, Check } from "lucide-react";
import { EcomFunnel } from "@/components/EcomFunnel";
import { ZernioConnectPanel, ZernioUpgradeModal } from "@/components/ZernioConnectPanel";
import { ShowcaseToggle } from "@/components/ShowcaseToggle";
import { KeywordCreditsWidget } from "@/components/KeywordCredits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const NICHES = ["E-commerce", "Gaming", "Local Services", "Real Estate", "Digital Products"];
const TIMEZONES = [
  { value: "America/Los_Angeles", label: "US Pacific (Los Angeles)" },
  { value: "America/Denver", label: "US Mountain (Denver)" },
  { value: "America/Chicago", label: "US Central (Chicago)" },
  { value: "America/New_York", label: "US Eastern (New York)" },
  { value: "America/Sao_Paulo", label: "Brazil (São Paulo)" },
  { value: "Europe/London", label: "UK (London)" },
  { value: "Europe/Paris", label: "Central Europe (Paris)" },
  { value: "Europe/Berlin", label: "Central Europe (Berlin)" },
  { value: "Europe/Madrid", label: "Spain (Madrid)" },
  { value: "Africa/Johannesburg", label: "South Africa (Johannesburg)" },
  { value: "Asia/Dubai", label: "Gulf (Dubai)" },
  { value: "Asia/Kolkata", label: "India (Kolkata)" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Tokyo", label: "Japan (Tokyo)" },
  { value: "Australia/Sydney", label: "Australia East (Sydney)" },
  { value: "UTC", label: "UTC" },
];
const PLATFORMS = ["facebook", "linkedin"];
const CONNECTABLE = ["facebook", "linkedin"];

export default function Settings() {
  const navigate = useNavigate();
  const { active, refresh } = useWorkspace();
  const [form, setForm] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [byok, setByok] = useState({});
  const [byokLi, setByokLi] = useState({});
  const [newAcct, setNewAcct] = useState({ platform: "facebook", zernio_account_id: "", label: "" });
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);
  const [limitModal, setLimitModal] = useState(null);
  const [storage, setStorage] = useState(null);
  const [reviewLink, setReviewLink] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [testingPost, setTestingPost] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const cleanupStorage = async () => {
    setCleaning(true);
    try {
      const r = await api.post("/storage/cleanup", { workspace_id: active.id });
      setStorage(r.data.usage);
      toast.success(r.data.deleted ? `Freed ${r.data.freed_gb} GB from ${r.data.deleted} old graphics` : "No old campaign media to clear");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not free space");
    } finally { setCleaning(false); }
  };
  const [siteUrl, setSiteUrl] = useState("");
  const [scanningSite, setScanningSite] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [connecting, setConnecting] = useState("");
  const [zernioAccts, setZernioAccts] = useState(null);
  const [zUpgrade, setZUpgrade] = useState(false);
  const [zkey, setZkey] = useState("");
  const [savingZkey, setSavingZkey] = useState(false);
  const [connectNote, setConnectNote] = useState(null);
  const [shop, setShop] = useState({ shop_url: "", access_token: "" });
  const [shopStatus, setShopStatus] = useState(null);
  const [shopBusy, setShopBusy] = useState("");
  useEffect(() => {
    if (active?.id) api.get(`/shopify/status?workspace_id=${active.id}`).then((r) => setShopStatus(r.data)).catch(() => {});
  }, [active?.id]);
  const connectShopify = async () => {
    setShopBusy("connect");
    try {
      const r = await api.post("/shopify/connect", { workspace_id: active.id, ...shop });
      toast.success(`Connected to ${r.data.shop_name}`);
      const s = await api.get(`/shopify/status?workspace_id=${active.id}`); setShopStatus(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect to Shopify."); }
    setShopBusy("");
  };
  const syncShopify = async () => {
    setShopBusy("sync");
    try {
      const r = await api.post(`/shopify/sync?workspace_id=${active.id}`);
      toast.success(`Synced ${r.data.synced} products · ${r.data.in_stock} in stock.`);
      const s = await api.get(`/shopify/status?workspace_id=${active.id}`); setShopStatus(s.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed."); }
    setShopBusy("");
  };
  const [testingWh, setTestingWh] = useState(false);
  const [whResult, setWhResult] = useState(null);

  useEffect(() => {
    if (!active) return;
    setForm({
      name: active.name, niche: active.niche, brand_name: active.brand_name,
      target_audience: active.target_audience || "", tone: active.tone || "",
      core_media_assets: active.core_media_assets || "",
      publish_webhook_url: active.publish_webhook_url || "",
      timezone: active.timezone || (Intl.DateTimeFormat().resolvedOptions().timeZone) || "America/New_York",
      auto_schedule: active.auto_schedule !== false,
    });
    setAccounts(active.connected_accounts || []);
    setByok(active.byok_twitter || {});
    setByokLi(active.byok_linkedin || {});
    api.get(`/social/health?workspace_id=${active.id}`).then((r) => setHealth(r.data)).catch(() => {});
    api.get(`/storage/usage?workspace_id=${active.id}`).then((r) => setStorage(r.data)).catch(() => {});
  }, [active]);

  const loadHealth = () => {
    if (active?.id) api.get(`/social/health?workspace_id=${active.id}`).then((r) => setHealth(r.data)).catch(() => {});
  };

  const createReviewLink = async () => {    setReviewBusy(true);
    try {
      const r = await api.post(`/workspaces/${active.id}/review-link`);
      const url = r.data.url?.startsWith("http") ? r.data.url : `${window.location.origin}/review/${r.data.review_token}`;
      setReviewLink(url);
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success("Client review link copied", { description: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create a review link");
    } finally { setReviewBusy(false); }
  };

  if (!active || !form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const acctLimit = health?.accounts_limit;
  const atLimit = acctLimit != null && accounts.length >= acctLimit;

  const addAccount = () => {
    if (atLimit) { setLimitModal({ title: "Account Limit Reached", message: `You've reached ${acctLimit} connected Facebook & LinkedIn profiles on your plan. Upgrade to the Agency tier for up to 25 profiles.` }); return; }
    if (!newAcct.zernio_account_id) { toast.error("Enter a Zernio account ID."); return; }
    setAccounts((a) => [...a, { ...newAcct }]);
    setNewAcct({ platform: "facebook", zernio_account_id: "", label: "" });
  };
  const removeAccount = (i) => setAccounts((a) => a.filter((_, idx) => idx !== i));

  const scanSite = async () => {
    if (!siteUrl.trim()) { toast.error("Enter your website URL."); return; }
    setScanningSite(true);
    try {
      const r = await api.post("/scan-url", { url: siteUrl.trim() });
      if (!r.data.success || !r.data.ok) { toast.error(r.data.error || "Couldn't read that site."); return; }
      setForm((f) => ({
        ...f,
        brand_name: f.brand_name || r.data.site_name || r.data.title || f.brand_name,
        core_media_assets: f.core_media_assets
          ? f.core_media_assets
          : [r.data.title, r.data.description].filter(Boolean).join(" — "),
      }));
      toast.success("Prefilled from your site — review and Save.");
    } catch (e) {
      toast.error("Couldn't reach that site.");
    } finally { setScanningSite(false); }
  };

  const addImported = (acct) => {
    if (atLimit) { setLimitModal({ title: "Account Limit Reached", message: `You've reached ${acctLimit} connected Facebook & LinkedIn profiles on your plan. Upgrade to the Agency tier for up to 25 profiles.` }); return; }
    if (accounts.some((a) => a.zernio_account_id === acct.zernio_account_id)) { toast.info("Already added."); return; }
    setAccounts((a) => [...a, acct]);
    toast.success(`Added ${acct.label || acct.platform}. Remember to Save.`);
  };

  const connect = async (platform) => {
    setConnecting(platform); setConnectNote(null);
    try {
      // 2 free Zernio channels: a 3rd connection routes through the affiliate upgrade.
      const st = (await api.get(`/v1/zernio/status?workspace_id=${active.id}`).catch(() => ({ data: null }))).data;
      if (st?.connected && (st.connected_accounts_count || 0) >= (st.free_limit || 2)) { setZUpgrade(true); return; }
      const r = await api.get(`/social/connect/${platform}?workspace_id=${active.id}`);
      if (r.data.authUrl) {
        window.open(r.data.authUrl, "_blank", "noopener");
        toast.success(`Opened ${platform} — approve access, then click "Refresh".`);
        setTimeout(refreshZernio, 1500);
      } else {
        setConnectNote({ error: r.data.error || `We couldn't finish connecting ${platform} right now. You can still publish instantly with Quick Publish below — no setup needed.` });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "object" ? d?.message : d);
      setConnectNote({ error: msg || "We couldn't finish connecting right now. You can still publish instantly with Quick Publish below — no setup needed." });
    } finally { setConnecting(""); }
  };

  const refreshZernio = async () => {
    try {
      const r = await api.get(`/social/accounts?workspace_id=${active.id}`);
      setZernioAccts(r.data);
      loadHealth();
      if (!r.data.length) toast.info("No connected accounts on Zernio yet.");
    } catch (e) { toast.error("Could not fetch connected accounts."); }
  };

  const saveZernioKey = async () => {
    if (!zkey.trim()) { toast.error("Paste your Zernio API key."); return; }
    setSavingZkey(true);
    try {
      await api.post(`/workspaces/${active.id}/zernio`, { api_key: zkey.trim() });
      toast.success("Zernio account connected — you can now link Facebook & LinkedIn.");
      setZkey(""); refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "object" ? d?.message : d) || "Could not verify that key.");
    } finally { setSavingZkey(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/workspaces/${active.id}`, {
        ...form, connected_accounts: accounts, byok_twitter: byok, byok_linkedin: byokLi,
      });
      toast.success("Settings saved.");
      refresh();
      loadHealth();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && (d.code === "profile_limit" || d.code === "seat_limit")) {
        setLimitModal({ title: d.code === "seat_limit" ? "Seat Limit Reached" : "Account Limit Reached", message: d.message });
      } else {
        toast.error((typeof d === "string" && d) || "Failed to save.");
      }
    } finally { setSaving(false); }
  };

  const testWebhook = async () => {
    const url = (form.publish_webhook_url || "").trim();
    if (!/^https?:\/\//.test(url)) { toast.error("Paste a valid https webhook URL first."); return; }
    setTestingWh(true); setWhResult(null);
    try {
      await api.put(`/workspaces/${active.id}`, { publish_webhook_url: url });
      refresh();
      const r = await api.post("/webhook/test", { workspace_id: active.id, url });
      if (r.data?.ok) { setWhResult({ ok: true }); toast.success("Test post delivered — auto-publish is connected."); }
      else { setWhResult({ ok: false, error: r.data?.error || `HTTP ${r.data?.status}` }); }
    } catch (e) {
      setWhResult({ ok: false, error: e?.response?.data?.detail || "Could not reach the webhook." });
    } finally { setTestingWh(false); }
  };
  const sendTestPost = async () => {
    setTestingPost(true); setTestResult(null);
    try {
      const r = await api.post("/social/test-publish", { workspace_id: active.id });
      setTestResult({ ok: true, ...r.data });
      if (r.data?.image_attached) toast.success("Test post sent with image to your connected pages.");
      else toast.info("Test post sent (no image).");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setTestResult({ ok: false, error: (typeof d === "object" ? d?.message : d) || "Test post failed." });
    } finally { setTestingPost(false); }
  };
  return (
    <div className="space-y-8 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Workspace Settings</h1>
          <p className="text-muted-foreground mt-1">{active.name}</p>
        </div>
        <Button data-testid="save-settings-button" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save
        </Button>
      </div>

      {/* Brand */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <h3 className="font-semibold">Brand profile</h3>
        <div className="rounded-lg border border-border bg-secondary/20 p-4 space-y-2">
          <Label className="flex items-center gap-1.5 text-sm"><Globe className="h-3.5 w-3.5 text-accent" /> Import from your website</Label>
          <p className="text-xs text-muted-foreground">Paste your site URL and we'll prefill your brand name and key facts from the page.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input data-testid="settings-site-url" type="url" value={siteUrl} onChange={(e) => setSiteUrl(e.target.value)} placeholder="https://yourbrand.com" className="flex-1" />
            <Button data-testid="settings-scan-site" onClick={scanSite} disabled={scanningSite} variant="outline" className="border-primary/40 text-primary shrink-0">
              {scanningSite ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Scan site
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Workspace name</Label>
            <Input data-testid="settings-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div className="space-y-2"><Label>Brand name</Label>
            <Input data-testid="settings-brand" value={form.brand_name} onChange={(e) => set("brand_name", e.target.value)} /></div>
          <div className="space-y-2"><Label>Niche</Label>
            <Select value={form.niche} onValueChange={(v) => set("niche", v)}>
              <SelectTrigger data-testid="settings-niche"><SelectValue /></SelectTrigger>
              <SelectContent>{NICHES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Tone of voice</Label>
            <Input data-testid="settings-tone" value={form.tone} onChange={(e) => set("tone", e.target.value)} /></div>
        </div>
        <div className="space-y-2"><Label>Target audience</Label>
          <Input data-testid="settings-audience" value={form.target_audience} onChange={(e) => set("target_audience", e.target.value)} /></div>
        <div className="space-y-2"><Label>Core media assets / key facts</Label>
          <Textarea data-testid="settings-assets" rows={4} value={form.core_media_assets} onChange={(e) => set("core_media_assets", e.target.value)} /></div>
      </div>

      {/* Posting schedule */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-5" data-testid="schedule-settings-card">
        <div>
          <h3 className="font-semibold">Posting schedule</h3>
          <p className="text-xs text-muted-foreground mt-1">We auto-schedule up to 4 posts/day at Facebook &amp; LinkedIn peak times — in your audience's timezone.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Audience timezone</Label>
            <Select value={form.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger data-testid="settings-timezone"><SelectValue placeholder="Pick a timezone" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {TIMEZONES.map((tz) => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Peak windows (e.g. LinkedIn 8am/12pm) are calculated in this zone.</p>
          </div>
          <div className="space-y-2">
            <Label>Hands-free auto-scheduling</Label>
            <div className="flex items-center gap-3 h-10">
              <Switch data-testid="settings-auto-schedule" checked={!!form.auto_schedule} onCheckedChange={(v) => set("auto_schedule", v)} />
              <span className="text-sm text-muted-foreground">{form.auto_schedule ? "On — posts auto-placed at peak times" : "Off — posts spaced one per day"}</span>
            </div>
          </div>
        </div>
      </div>

      {/* #1 — Connect via posting partner (Zernio) */}
      <div className="rounded-xl border border-primary/40 bg-card p-6 space-y-4" data-testid="connect-card">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Connect your social accounts</h3>
          <Badge className="bg-primary/15 text-primary border-primary/30">Recommended</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Authorize your Facebook or LinkedIn <span className="text-foreground font-medium">once</span> and every scheduled post
          publishes itself — fully hands-off. Your login stays with the platform; we never see your password.
        </p>

        <ZernioConnectPanel workspaceId={active?.id} onChange={refresh} />
        <ZernioUpgradeModal open={zUpgrade} onOpenChange={setZUpgrade} />
        <ShowcaseToggle />
        <KeywordCreditsWidget />

        {health && health.accounts_limit != null && (
          <p className="text-xs text-muted-foreground" data-testid="account-usage">
            <Activity className="h-3.5 w-3.5 inline mr-1 text-primary" />
            {accounts.length} of {health.accounts_limit} profiles connected on your {health.plan} plan
            {health.seats_limit != null ? ` · ${health.seats_limit} team seat${health.seats_limit !== 1 ? "s" : ""}` : ""}
          </p>
        )}

        {health && health.needs_attention > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2.5" data-testid="reauth-banner">
            <ShieldAlert className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300">
              {health.needs_attention} connection{health.needs_attention !== 1 ? "s need" : " needs"} attention — reconnect below to keep auto-publishing running.
            </p>
          </div>
        )}

        {accounts.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-foreground flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-primary" /> Connection health</p>
            {accounts.map((a, i) => {
              const h = (health?.accounts || []).find((x) => x.zernio_account_id === a.zernio_account_id) || {};
              const st = h.status || "connected";
              const cfg = st === "reauth"
                ? { wrap: "border-rose-500/40 bg-rose-500/5", badge: "bg-rose-500/15 text-rose-400 border-rose-500/40", dot: "bg-rose-400", label: "Re-authentication required", icon: ShieldAlert }
                : st === "expiring"
                ? { wrap: "border-amber-500/40 bg-amber-500/5", badge: "bg-amber-500/15 text-amber-400 border-amber-500/40", dot: "bg-amber-400", label: "Token expiring soon", icon: AlertTriangle }
                : { wrap: "border-emerald-500/30 bg-emerald-500/5", badge: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40", dot: "bg-emerald-400", label: "Connected", icon: CheckCircle2 };
              const StIcon = cfg.icon;
              return (
                <div key={i} className={`flex items-center gap-3 rounded-lg border ${cfg.wrap} p-3`} data-testid={`connected-account-${i}`}>
                  <Badge className="bg-primary/15 text-primary border-primary/30 capitalize">{a.platform}</Badge>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{a.label || a.platform}</div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-1"><span className={`inline-block h-1.5 w-1.5 rounded-full ${cfg.dot}`} /> {h.detail || "Connected · auto-publishing on."}</div>
                  </div>
                  <Badge className={`${cfg.badge} shrink-0 hidden sm:inline-flex`} data-testid={`account-status-${i}`}><StIcon className="h-3 w-3 mr-1" /> {cfg.label}</Badge>
                  {st === "reauth" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-rose-400/40 text-rose-300 shrink-0" data-testid={`reconnect-account-${i}`} onClick={() => connect(a.platform)}>
                      <RefreshCw className="h-3 w-3 mr-1" /> Reconnect
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="text-rose-400 h-8 w-8 shrink-0" data-testid={`remove-account-${i}`} onClick={() => removeAccount(i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              );
            })}
          </div>
        )}

        {accounts.length > 0 && (
          <div className="space-y-3 rounded-lg border border-border bg-secondary/20 p-4" data-testid="test-publish-block">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Send a test post</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Publishes one sample post (with a branded image) to your connected Facebook/LinkedIn pages right now, so you can confirm the image attaches.
                </p>
              </div>
              <Button data-testid="send-test-post-btn" onClick={sendTestPost} disabled={testingPost}
                className="bg-primary hover:bg-primary/90 text-white shrink-0">
                {testingPost ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                Send test post
              </Button>
            </div>
            {testResult && (
              testResult.ok ? (
                <div className="text-xs rounded-md border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 px-3 py-2 space-y-1" data-testid="test-post-result-ok">
                  <div className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Test post sent to {testResult.accounts?.length || 0} account(s).</div>
                  <div>Image attached: <span className="font-semibold">{testResult.image_attached ? "yes" : "no"}</span>
                    {testResult.image_reachable === false && <span className="text-amber-300"> · image URL not publicly reachable — check PUBLIC_BACKEND_URL</span>}</div>
                  {(testResult.post_urls || []).length > 0 && (
                    <div className="flex flex-col gap-0.5">
                      {testResult.post_urls.map((u) => (
                        <a key={u} href={u} target="_blank" rel="noopener noreferrer" className="underline break-all hover:text-emerald-200">{u}</a>
                      ))}
                    </div>
                  )}
                  {(testResult.failures || []).length > 0 && (
                    <div className="text-amber-300">Some platforms failed: {testResult.failures.map((f) => `${f.platform || "?"}: ${f.error || "rejected"}`).join("; ")}</div>
                  )}
                </div>
              ) : (
                <div className="text-xs rounded-md border border-rose-500/30 bg-rose-500/5 text-rose-300 px-3 py-2 flex items-start gap-1.5" data-testid="test-post-result-error">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {testResult.error}
                </div>
              )
            )}
          </div>
        )}

        <div className="space-y-3 rounded-lg border border-border bg-secondary/20 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium flex items-center gap-2"><Link2 className="h-4 w-4 text-accent" /> Connect a platform</span>
            <Button data-testid="refresh-connected-button" onClick={refreshZernio} variant="ghost" size="sm" className="text-xs text-primary">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {CONNECTABLE.map((p) => (
              <Button key={p} data-testid={`connect-${p}`} onClick={() => connect(p)} disabled={connecting === p}
                variant="outline" size="sm" className="capitalize border-primary/40 text-foreground hover:bg-primary/10">
                {connecting === p ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
                {p}
              </Button>
            ))}
          </div>
          {connectNote && (
            <div className="text-xs rounded-md border border-border bg-secondary/40 text-muted-foreground px-3 py-2" data-testid="connect-note">
              {connectNote.error}
            </div>
          )}

          {/* Passwordless client review link (magic link, scoped to this brand) */}
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="review-link-card">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Client review link</span>
              <Button data-testid="create-review-link" onClick={createReviewLink} disabled={reviewBusy} size="sm" variant="outline" className="border-primary/40 text-primary text-xs">
                {reviewBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />} {reviewLink ? "Copy again" : "Create link"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">A passwordless link where your client reviews only <span className="text-foreground font-medium">{active.brand_name || active.name}</span>'s upcoming posts — approve or leave feedback in one tap, no account needed.</p>
            {reviewLink && <div className="text-xs font-mono text-primary break-all bg-background/60 rounded px-2 py-1.5" data-testid="review-link-url">{reviewLink}</div>}
          </div>
          {zernioAccts && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Your connected accounts — click Use to publish from them:</p>
              {zernioAccts.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nothing connected yet. Click a platform above, authorize, then Refresh.</p>
              ) : zernioAccts.map((a) => (
                <div key={a.zernio_account_id} className="flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-2.5" data-testid={`zernio-account-${a.zernio_account_id}`}>
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/40 capitalize">{a.platform}</Badge>
                  <span className="text-sm truncate flex-1 min-w-0">{a.label || a.zernio_account_id}</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-primary/40 text-primary"
                    data-testid={`use-account-${a.zernio_account_id}`} onClick={() => addImported(a)}>
                    <Plus className="h-3 w-3 mr-1" /> Use
                  </Button>
                </div>
              ))}
            </div>
          )}
          <button type="button" data-testid="toggle-manual-account" onClick={() => setShowManual((s) => !s)}
            className="text-xs text-muted-foreground hover:text-foreground underline">
            {showManual ? "Hide advanced options" : "Advanced options"}
          </button>
          {showManual && (
            <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr_1fr_auto] gap-2 items-end">
              <div className="space-y-1"><Label className="text-xs">Platform</Label>
                <Select value={newAcct.platform} onValueChange={(v) => setNewAcct((n) => ({ ...n, platform: v }))}>
                  <SelectTrigger data-testid="new-account-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label className="text-xs">Account ID</Label>
                <Input data-testid="new-account-id" value={newAcct.zernio_account_id} onChange={(e) => setNewAcct((n) => ({ ...n, zernio_account_id: e.target.value }))} placeholder="acc_xxx" /></div>
              <div className="space-y-1"><Label className="text-xs">Label</Label>
                <Input data-testid="new-account-label" value={newAcct.label} onChange={(e) => setNewAcct((n) => ({ ...n, label: e.target.value }))} placeholder="@handle" /></div>
              <Button data-testid="add-account-button" onClick={addAccount} variant="outline" className="border-primary/40 text-primary"><Plus className="h-4 w-4" /></Button>
            </div>
          )}
        </div>

        <details className="group" data-testid="manual-publish-details">
          <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground list-none flex items-center gap-2">
            <ExternalLink className="h-3.5 w-3.5" /> Prefer to post manually? Use Quick Publish (no connection)
          </summary>
          <div className="mt-3 rounded-lg border border-border bg-secondary/20 p-4" data-testid="no-connect-banner">
            <p className="text-xs text-muted-foreground">
              One tap to <span className="font-medium text-foreground">Copy</span> the caption and <span className="font-medium text-foreground">Open in X / LinkedIn / Facebook</span> with everything pre-filled. You're already logged into your socials, so just review and post.
            </p>
            <Button size="sm" onClick={() => navigate("/composer")} className="mt-3 bg-primary hover:bg-primary/90 text-white h-8 text-xs" data-testid="goto-composer-btn">
              Go to Composer
            </Button>
          </div>
        </details>
      </div>

      {/* Media storage meter */}
      {storage && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-3" data-testid="storage-meter">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /> Media storage</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground" data-testid="storage-usage-text">{storage.used_gb} GB / {storage.limit_gb} GB</span>
              <Button size="sm" variant="outline" onClick={cleanupStorage} disabled={cleaning} className="h-7 text-xs border-border" data-testid="storage-cleanup-btn">
                {cleaning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1.5" />} Free up space
              </Button>
            </div>
          </div>
          <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
            <div className={`h-full rounded-full transition-all ${storage.pct >= 90 ? "bg-rose-500" : storage.pct >= 70 ? "bg-amber-400" : "bg-primary"}`} style={{ width: `${Math.max(2, storage.pct)}%` }} data-testid="storage-bar" />
          </div>
          <p className="text-xs text-muted-foreground">
            {storage.over
              ? "Storage full — delete old campaigns or upgrade to Agency (50GB) to render new image posts."
              : storage.pct >= 70
              ? `You've used ${storage.pct}% of your media storage. Consider upgrading before it fills up.`
              : `Branded post images and product photos count toward your ${storage.limit_gb}GB plan storage.`}
          </p>
        </div>
      )}

      {/* BYOK Twitter */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-amber-400" /> Advanced: use your own X (Twitter) API keys</h3>
        <p className="text-xs text-muted-foreground">Optional. Bring your own X API keys to post to X without extra pass-through fees. Skip this if you're not sure.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ["api_key", "API Key"], ["api_secret", "API Secret"],
            ["access_token", "Access Token"], ["access_token_secret", "Access Token Secret"],
          ].map(([k, label]) => (
            <div key={k} className="space-y-2">
              <Label className="flex items-center gap-1.5"><Twitter className="h-3.5 w-3.5" /> {label}</Label>
              <Input data-testid={`byok-${k}`} type="password" value={byok[k] || ""} onChange={(e) => setByok((b) => ({ ...b, [k]: e.target.value }))} placeholder="••••••••" />
            </div>
          ))}
        </div>
      </div>

      {/* BYOK LinkedIn */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-sky-400" /> Advanced: use your own LinkedIn keys</h3>
        <p className="text-xs text-muted-foreground">Optional. Paste a LinkedIn access token and your author URN to auto-publish to LinkedIn directly — no Zernio.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            ["access_token", "Access Token", "••••••••", "password"],
            ["author_urn", "Author URN", "urn:li:person:xxxxxxxx", "text"],
          ].map(([k, label, ph, type]) => (
            <div key={k} className="space-y-2">
              <Label className="flex items-center gap-1.5"><Linkedin className="h-3.5 w-3.5" /> {label}</Label>
              <Input data-testid={`byok-li-${k}`} type={type} value={byokLi[k] || ""} onChange={(e) => setByokLi((b) => ({ ...b, [k]: e.target.value }))} placeholder={ph} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-6 space-y-3" data-testid="shopify-card">
        <h3 className="font-semibold flex items-center gap-2"><Store className="h-4 w-4 text-primary" /> Shopify catalog sync</h3>
        {shopStatus?.connected ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-emerald-400" data-testid="shopify-connected"><CheckCircle2 className="h-4 w-4" /> Connected to {shopStatus.shop_name || shopStatus.shop_url}</div>
            <div className="text-xs text-muted-foreground">
              {shopStatus.products || 0} products · <span className="text-emerald-400">{shopStatus.in_stock || 0} in stock</span> · <span className="text-rose-400">{shopStatus.out_of_stock || 0} out of stock</span>
              {shopStatus.last_synced_at ? ` · synced ${new Date(shopStatus.last_synced_at).toLocaleString()}` : ""}
            </div>
            <p className="text-xs text-muted-foreground">Out-of-stock products are automatically dropped from upcoming posts and swapped for in-stock SKUs.</p>
            <Button data-testid="shopify-sync-btn" onClick={syncShopify} disabled={shopBusy === "sync"} className="bg-primary hover:bg-primary/90 text-white h-9 text-sm">
              {shopBusy === "sync" ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Syncing…</> : <><RefreshCw className="h-4 w-4 mr-2" />Sync products now</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Quick Connect — paste your store domain and an Admin API access token (needs <code className="text-accent">read_products</code>). We keep your catalog and stock in sync so posts never promote sold-out items.</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input data-testid="shopify-shop-url" value={shop.shop_url} onChange={(e) => setShop((s) => ({ ...s, shop_url: e.target.value }))} placeholder="your-store.myshopify.com" className="flex-1" />
              <Input data-testid="shopify-token" type="password" value={shop.access_token} onChange={(e) => setShop((s) => ({ ...s, access_token: e.target.value }))} placeholder="Admin API access token" className="flex-1" />
              <Button data-testid="shopify-connect-btn" onClick={connectShopify} disabled={shopBusy === "connect"} className="bg-primary hover:bg-primary/90 text-white shrink-0">
                {shopBusy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <EcomFunnel workspaceId={active?.id} />

      <div className="rounded-xl border border-border bg-card p-6 space-y-3">
        <h3 className="font-semibold flex items-center gap-2"><Zap className="h-4 w-4 text-primary" /> Connect auto-publish (n8n / QStash)</h3>
        <p className="text-xs text-muted-foreground">
          Paste your automation webhook and we'll auto-publish each post at its scheduled time to your connected socials
          (your n8n / QStash / Zapier flow holds the logins). It fires a <code className="text-accent">post_publish</code> event per post.
          Leave blank to publish manually with Quick Publish.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="settings-publish-webhook"
            type="url"
            className="flex-1"
            value={form.publish_webhook_url || ""}
            onChange={(e) => { set("publish_webhook_url", e.target.value); setWhResult(null); }}
            placeholder="https://your-n8n-instance.com/webhook/abc123"
          />
          <Button data-testid="settings-webhook-test" onClick={testWebhook} disabled={testingWh}
            variant="outline" className="border-primary/40 text-primary shrink-0">
            {testingWh ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : "Send test post"}
          </Button>
        </div>
        {whResult && (
          <div data-testid="settings-webhook-result"
            className={`text-xs rounded-md px-3 py-2 border ${whResult.ok ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-rose-500/40 bg-rose-500/10 text-rose-300"}`}>
            {whResult.ok
              ? "Success — your webhook received the test post. Auto-publish is connected and every queued post will publish itself at its scheduled time."
              : `Test failed: ${whResult.error}. Check the URL is live and accepts POST JSON, then try again.`}
          </div>
        )}
      </div>

      {/* Account / seat limit modal */}
      <Dialog open={!!limitModal} onOpenChange={(o) => !o && setLimitModal(null)}>
        <DialogContent data-testid="limit-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-400" /> {limitModal?.title}</DialogTitle>
            <DialogDescription>{limitModal?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="border-border" onClick={() => setLimitModal(null)} data-testid="limit-modal-close">Close</Button>
            <Button className="bg-primary hover:bg-primary/90 text-white" onClick={() => { setLimitModal(null); navigate("/billing"); }} data-testid="limit-modal-upgrade">Upgrade plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
